import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { validateFieldValue } from '@/utils/field-formatters'

import { RecordFieldInput } from '../RecordFieldInput'

/** Field types a spreadsheet cell can edit in place; everything else opens the record. */
export const INLINE_EDIT_TYPES: ReadonlySet<string> = new Set([
  FormFieldType.TEXT, FormFieldType.EMAIL, FormFieldType.URL, FormFieldType.NUMBER,
  FormFieldType.CURRENCY, FormFieldType.PERCENT, FormFieldType.DATE, FormFieldType.TIME,
  FormFieldType.SELECT, FormFieldType.RADIO, FormFieldType.TOGGLE,
])

/** Types that pick from a list: they save as soon as a choice is made instead of on blur. */
const COMMIT_ON_CHANGE: ReadonlySet<string> = new Set([FormFieldType.SELECT, FormFieldType.RADIO, FormFieldType.TOGGLE])

/** A table cell: shows the formatted value; double-click turns it into the field's input. */
export function InlineCell({ field, value, display, onCommit }: {
  field: FormField
  /** Raw stored value. */
  value: string
  /** Formatted value shown while not editing. */
  display: string
  onCommit: (value: string) => Promise<void>
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const editing = draft !== null
  // Put the caret in the input as soon as the cell turns into one.
  useEffect(() => {
    if (editing) box.current?.querySelector<HTMLElement>('input, textarea, [role=combobox], button')?.focus()
  }, [editing])

  const commit = async (next: string) => {
    setDraft(null)
    if (next === value) return
    const problem = validateFieldValue(next, field.type, field.required, field.validations, field.options)
    if (problem) {
      toast.error(`${field.label}: ${problem}`)
      return
    }
    setSaving(true)
    try {
      await onCommit(next)
    } catch {
      toast.error(`Could not save ${field.label}`)
    } finally {
      setSaving(false)
    }
  }

  if (draft === null) {
    return (
      <span
        className={saving ? 'opacity-50' : 'cursor-text'}
        title="Double-click to edit"
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => { event.stopPropagation(); setDraft(value) }}
      >
        {display}
      </span>
    )
  }
  return (
    <div
      ref={box}
      className="min-w-40"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setDraft(null)
        if (event.key === 'Enter' && field.type !== FormFieldType.PARAGRAPH) void commit(draft)
      }}
      onBlur={(event) => {
        // Moving focus inside the cell (e.g. into a dropdown) is not leaving it.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null) && !COMMIT_ON_CHANGE.has(field.type)) {
          void commit(draft)
        }
      }}
    >
      <RecordFieldInput
        field={field}
        value={draft}
        error={null}
        onChange={(next) => {
          if (COMMIT_ON_CHANGE.has(field.type)) void commit(next)
          else setDraft(next)
        }}
        id={`inline-${field.id}`}
      />
    </div>
  )
}
